import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import BaseCheckbox from '@r1-frontend/ui-react/components/controls/baseCheckbox';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import DefaultPopup from '@r1-frontend/ui-react/components/popups/defaultPopup';
import { CallCalling } from '@r1-frontend/ui-react/components/svg/call';
import { RoundedBlock } from '@r1-frontend/ui-react/experimental/blocks';
import SpeedLineWithDescription, {
    TSpeedLineItemWithDescription,
} from '@r1-frontend/ui-react/organisms/SpeedLine/SpeedLineWithDescription';
import { ACTIONS, CATEGORIES } from '@r1-frontend/shared/constants/analytics';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';
import { useOnceExecute } from '@r1-frontend/shared/hooks/useOnceExecute';
import { TArrayTwoOrMore } from '@r1-frontend/shared/types/Array';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import LazyCallBack from '~/src/components/LazyCallBack';
import isFullBuyVillage from '~/src/features/full-buy/helpers/ab/isFullBuyVillage';
import getUriToFullBuy from '~/src/features/request/helpers/getUriToFullBuy';
import { TTariffsGroup } from '~/src/features/request/types/TTariffsGroup';
import { getAnalyticsSender } from '~/src/helpers/hooks/useAnaliticsSender';
import { isLeadGenerationHide } from '~/src/helpers/isLeadGenerationHide';

import * as ST from '~/src/features/internet/internet-represent/page-blocks/RepresentTop/styled';

type TSpeedLineSelectorProps = {
    speedLineItems: {
        bundles: TArrayTwoOrMore<TSpeedLineItemWithDescription>,
        internet: TArrayTwoOrMore<TSpeedLineItemWithDescription>,
    },
    isAuth: boolean,
    isCottage: boolean,
}
const sendAnalytics = getAnalyticsSender(CATEGORIES.applicationForm);
const sendAnalyticsCard = getAnalyticsSender(CATEGORIES.tariffCards);
const SpeedLineSelector = (
    {
        speedLineItems,
        isAuth,
        isCottage,
    }: TSpeedLineSelectorProps,
): JSX.Element => {
    const router = useRouter();
    const isTablet = useMatchMedia(`(max-width: ${wideBreakpoints.tablet})`);
    const [selectedSpeedItemId, setSelectedSpeedItemId] = useState(0);
    const [isLazyCallbackOpen, setIsLazyCallbackOpen] = useState(false);

    const sendAnalyticsOnce = useOnceExecute(() => sendAnalyticsCard(ACTIONS.moveSpeedSlider));
    selectedSpeedItemId && sendAnalyticsOnce();

    const [tariffsGroup, setTariffsGroup] = useState<TTariffsGroup>(speedLineItems.bundles.length > 0 ? 'bundles' : 'internet');

    const selectedSpeedItem = speedLineItems[tariffsGroup][selectedSpeedItemId];
    const redirectLink = isAuth ? '/lk/change-tariff' : getUriToFullBuy(selectedSpeedItem.alias, tariffsGroup);

    const showLazyCallback = () => {
        sendAnalytics(ACTIONS.openCallbackForm);
        if (isFullBuyVillage()) {
            router.push(redirectLink.concat('&filters=cottage'));
        } else {
            setIsLazyCallbackOpen(true);
        }
    };

    const isLeadGeneration = useMemo(() => isLeadGenerationHide(), []);
    const handleSetIsTvChannelsNeed = () => {
        setTariffsGroup(prev => prev === 'bundles' ? 'internet' : 'bundles');
        tariffsGroup === 'bundles'
            ? sendAnalyticsCard(ACTIONS.checkboxTvChannelsOff)
            : sendAnalyticsCard(ACTIONS.checkboxTvChannelsOn);
        setSelectedSpeedItemId(0);
    };

    const onClickHandler = () => {
        !isAuth && sendAnalytics(
            'open_tariff',
            `${selectedSpeedItem.title}|${tariffsGroup === 'bundles' ? 'bundle' : 'mono'}`,
        );
    };
    // В связи с тем, что наборы тарифов с ТВ и без ТВ могут различаться в количестве и конечным составом, было принято решение вывести
    // два компонента выбора скорости под каждый набор. Таким образом с компонента снята ответственность перерендера с новым набором
    // и обнуление состояния выбранного элемента.
    // Это необходимо в связи с тем, что есть внешний контрол (с ТВ, без ТВ), который влияет на набор тарифов.
    // Вывести два компонента гораздо дешевле чем монструозить логику сброса состояния выбранной скорости в зависимости от нового набора данных.
    return (
        <>
            <ST.SpeedLineSelectorWrapper>
                {tariffsGroup === 'bundles'
                    ? (
                        <SpeedLineWithDescription
                            key={'SpeedLineWithDescription_bundles'}
                            items={speedLineItems.bundles}
                            onSelect={setSelectedSpeedItemId}
                            title={'Какая скорость интернета вам нужна?'}
                            startDot={0}
                        />
                    )
                    : (
                        <SpeedLineWithDescription
                            key={'SpeedLineWithDescription_internet'}
                            items={speedLineItems.internet}
                            onSelect={setSelectedSpeedItemId}
                            title={'Какая скорость интернета вам нужна?'}
                            startDot={0}
                        />
                    )
                }

                {speedLineItems.bundles.length > 0 && (
                    <RoundedBlock>
                        <BaseCheckbox checked={tariffsGroup === 'bundles'} onChange={handleSetIsTvChannelsNeed}>
                            А еще мне нужны ТВ-каналы
                        </BaseCheckbox>
                    </RoundedBlock>
                )}

                <ST.ButtonsWrapper>
                    {isCottage
                        ? (
                            <Button bType={'action'} onClick={showLazyCallback}>Подключить</Button>
                        )
                        : (
                            <>
                                <Link href={redirectLink} passHref>
                                    <Button bType={'action'} onClick={onClickHandler}>Подключить</Button>
                                </Link>
                                {!isLeadGeneration && <Button bType={'secondary'} rounded Icon={<CallCalling/>} onClick={showLazyCallback}/>}
                            </>
                        )
                    }
                </ST.ButtonsWrapper>
            </ST.SpeedLineSelectorWrapper>

            <DefaultPopup
                isOpen={isLazyCallbackOpen}
                onClose={() => setIsLazyCallbackOpen(false)}
                width={isTablet ? '360px' : '600px'}
            >
                <LazyCallBack
                    tariffName={selectedSpeedItem?.title}
                    requestDataParams={{
                        product_id: tariffsGroup === 'bundles'
                            ? [EProductsId.internet, EProductsId.tv]
                            : [EProductsId.internet],
                    }}
                    withPopupResult={true}
                    title={isCottage
                        ? 'Поможем оформить заявку на подключение по телефону'
                        : 'Если не хотите заполнять заявку сами'
                    }
                    description={isCottage
                        ? <>Подскажем подходящий тариф и подберём роутер</>
                        : <>Можно сделать это по&nbsp;телефону. Заодно поможем выбрать роутер.</>
                    }
                />
            </DefaultPopup>
        </>
    );
};

export default SpeedLineSelector;
